import React, { useEffect } from "react";
import { Box, Typography, Button, styled } from "@mui/material";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const BecomePartnerSection = styled(Box)(({ theme }) => ({
  padding: "64px 98px",
  background: "#FFFFFF",
  borderRadius: "48px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  overflow: "hidden",
  margin: "128px auto",
  maxWidth: "1400px",
  [theme.breakpoints.down("lg")]: {
    padding: "48px 48px",
    margin: "48px 20px",
  },
  [theme.breakpoints.down("md")]: {
    padding: "32px 24px",
    margin: "32px 16px",
  },
}));

const BecomePartnerBackground = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "-393px",
  left: "-19px",
  width: "1680px",
  height: "1190px",
  backgroundImage: "url(/images/partner-section/background.jpg)",
  backgroundSize: "contain",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  zIndex: 0,
}));

const BecomePartnerContent = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "1109px",
  maxWidth: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1,
}));

const BecomePartnerEllipse = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "-48px",
  left: "50%",
  transform: "translateX(-50%)",
  width: "544px",
  height: "342px",
  backgroundImage: "url(/images/partner-section/ellipse.svg)",
  backgroundSize: "contain",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  mixBlendMode: "lighten",
  zIndex: 0,
  [theme.breakpoints.down("lg")]: {
    width: "400px",
    height: "250px",
    top: "-30px",
  },
  [theme.breakpoints.down("md")]: {
    width: "300px",
    height: "180px",
    top: "-20px",
  },
}));

const BecomePartnerInner = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "32px",
  padding: "48px 48px",
  position: "relative",
  zIndex: 1,
  textAlign: "center",
  [theme.breakpoints.down("md")]: {
    padding: "24px 24px",
    gap: "24px",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(255, 255, 255, 0.85)",
      backdropFilter: "blur(8px)",
      borderRadius: "24px",
      zIndex: -1,
    },
  },
  [theme.breakpoints.down("sm")]: {
    "&::before": {
      background: "rgba(255, 255, 255, 0.7)",
      backdropFilter: "blur(12px)",
    },
  },
}));

const BecomePartnerText = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "12px",
  color: "#3E4044",
}));

const BecomePartnerButton = styled(Button)(({ theme }) => ({
  background: "#F5933D",
  color: "#FFFFFF",
  borderRadius: "12px",
  padding: "16px 40px",
  fontSize: "16px",
  fontWeight: 600,
  fontFamily: "Inter",
  textTransform: "none",
  minWidth: "200px",
  "&:hover": {
    background: "#E8843A",
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(245, 147, 61, 0.3)",
  },
  transition: "all 0.3s ease",
}));

const BecomePartnerSectionComponent: React.FC = () => {
  const { t, i18n } = useTranslation();

  return (
    <BecomePartnerSection>
      <BecomePartnerBackground />
      <BecomePartnerContent>
        <BecomePartnerEllipse />
        <BecomePartnerInner>
          <BecomePartnerText>
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: { xs: "20px", sm: "22px", md: "24px", lg: "28px" },
                lineHeight: 1.4,
                color: "#3E4044",
                textAlign: "center",
              }}
            >
              {t('common.becomePartnerSection.title')}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 400,
                fontSize: { xs: "16px", md: "18px" },
                lineHeight: 1.5,
                color: "#3E4044",
                textAlign: "center",
                maxWidth: "600px",
              }}
            >
              {t('common.becomePartnerSection.description')}
            </Typography>
          </BecomePartnerText>
          <Link style={{ textDecoration: "none", color: "#FFFFFF" }} to={"/partner-application"}>
            <BecomePartnerButton>{t('common.becomePartnerSection.joinButton')}</BecomePartnerButton>
          </Link>
        </BecomePartnerInner>
      </BecomePartnerContent>
    </BecomePartnerSection>
  );
};

export default BecomePartnerSectionComponent;
